import { Autocomplete, Box, Button, Card, CardContent, Checkbox, FormControlLabel, IconButton, TextField, Typography, Switch, Grid2 } from "@mui/material";

import { Container, ContainerConfig } from "./styles";
import { useEffect, useState } from "react";
import { initialStateAssistantAutocomplete } from "../../services/configAssistent";
import { GET_DATA, POST_DATA, SalvarLogErro } from "../../services/API";
import LoadingAll from "../../components/loadingAll";
import Swal from "sweetalert2";
import { Editor } from "@monaco-editor/react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";

const buildDefaultPromptConfig = (assistantName = "Assistente Virtual Avançado") => {
    return {
        nome: assistantName,
        modelo: "gpt-4o",
        temperatura: 0.7,
        // mantendo o nome que você usou no exemplo ("instrunction")
        instrunction: "",
        notInstruction: "",
        tools: [],
        integracao: {
            url: "",
            metodo: "POST",
            headers: [],
            body: {
                tipoRequisicao: "JSON"
            },
            funcoes: []
        },
        sessoes: [
            {
                default: true,
                titulo: "Notificação",
                palavrasChave: [],
                agenteResponsavel: { nome: "Vita", idVoz: "pt-BR-AntonioNeural" },
                instrunction: "Sessão padrão de notificações. Sempre deve estar ativa.",
                ativo: true
            },
            {
                default: false,
                titulo: "Alimentação",
                palavrasChave: ["nutrição", "dieta", "comida", "refeição", "calorias", "proteína"],
                agenteResponsavel: { nome: "Atlanta", idVoz: "pt-BR-AntonioNeural" },
                instrunction: "Regras específicas para alimentação.",
                ativo: true
            },
            {
                default: false,
                titulo: "Performance",
                palavrasChave: ["treino", "corrida", "pace", "km", "vo2", "ritmo"],
                agenteResponsavel: { nome: "Hermes", idVoz: "pt-BR-AntonioNeural" },
                instrunction: "Regras específicas para performance.",
                ativo: true
            }
        ]
    };
};

const parsePalavrasChaveToString = (arr) => {
    if (!Array.isArray(arr)) return "";
    return arr.join(", ");
};

const parsePalavrasChaveToArray = (str) => {
    if (!str) return [];
    return str
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
};

const ConfigPromptJson = () => {
    const [assistants, setAssistants] = useState([
        { id: 0, assistantId: "", label: "Selecione um agente", disabled: true }
    ]);

    const [assistantAutocomplete, setAssistantAutocomplete] = useState(initialStateAssistantAutocomplete);
    const [promptConfig, setPromptConfig] = useState(null);
    const [loading, setLoading] = useState(false);

    const themeSelected = useSelector((state) => state.theme.dados.theme) || "vs-dark";

    // Busca lista básica de agentes (igual na tela atual)
    const fetchAssistants = async () => {
        try {
            const result = await GET_DATA("Assistant/GetAllAssistantBasicData?flgActive=true");

            if (result !== null) {
                const options = [];

                result.data.map((item) => {
                    options.push({
                        id: item.id,
                        label: item.name,
                        assistantId: item.assistantId,
                        disabled: false
                    });
                    return null;
                });

                setAssistants(options);
            }
        } catch (err) {
            SalvarLogErro("ConfigPromptJson - fetchAssistants", err);
        }
    };

    useEffect(() => {
        fetchAssistants();
    }, []);

    // Carrega o PromptJson de um agente
    const loadPromptConfig = async (assistantSelected) => {
        if (!assistantSelected || !assistantSelected.id) return;

        try {
            setLoading(true);

            // você pode usar o mesmo endpoint da outra tela
            const result = await GET_DATA(`Assistant/GetAssistantById?id=${assistantSelected.id}`);

            if (result !== null && result.data) {
                const data = result.data;
                let config = null;

                if (data.promptJson && data.promptJson.length > 0) {
                    try {
                        config = JSON.parse(data.promptJson);
                    } catch (e) {
                        console.warn("Erro ao fazer parse do PromptJson, carregando default.", e);
                        config = buildDefaultPromptConfig(data.name);
                    }
                } else {
                    config = buildDefaultPromptConfig(data.name);
                }

                setPromptConfig(config);
                setAssistantAutocomplete({
                    id: data.id,
                    assistantId: data.assistantId,
                    label: data.name,
                    disabled: false
                });
            }
        } catch (err) {
            SalvarLogErro("ConfigPromptJson - loadPromptConfig", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAssistant = (_, newValue) => {
        if (!newValue || !newValue.id) return;
        loadPromptConfig(newValue);
    };

    // Handlers de edição do objeto promptConfig -----------------------------

    const updatePromptConfig = (field, value) => {
        setPromptConfig((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const updateIntegracao = (field, value) => {
        setPromptConfig((prev) => ({
            ...prev,
            integracao: {
                ...(prev?.integracao || {}),
                [field]: value
            }
        }));
    };

    const updateIntegracaoBody = (field, value) => {
        setPromptConfig((prev) => ({
            ...prev,
            integracao: {
                ...(prev?.integracao || {}),
                body: {
                    ...(prev?.integracao?.body || {}),
                    [field]: value
                }
            }
        }));
    };

    const updateSessao = (index, field, value) => {
        setPromptConfig((prev) => {
            const sessoes = [...(prev?.sessoes || [])];
            const sessao = { ...(sessoes[index] || {}) };

            if (field === "palavrasChave") {
                sessao.palavrasChave = parsePalavrasChaveToArray(value);
            } else if (field.startsWith("agenteResponsavel.")) {
                const subField = field.split(".")[1];
                sessao.agenteResponsavel = {
                    ...(sessao.agenteResponsavel || {}),
                    [subField]: value
                };
            } else {
                sessao[field] = value;
            }

            sessoes[index] = sessao;
            return {
                ...prev,
                sessoes
            };
        });
    };

    const handleToggleDefaultSessao = (idx, checked) => {
        setPromptConfig((prev) => {
            const sessoes = (prev?.sessoes || []).map((s, index) => ({
                ...s,
                default: index === idx ? checked : false
            }));
            return {
                ...prev,
                sessoes
            };
        });
    };

    const handleToggleAtivoSessao = (idx, checked) => {
        setPromptConfig((prev) => {
            const sessoes = [...(prev?.sessoes || [])];
            if (!sessoes[idx]) return prev;
            sessoes[idx] = { ...sessoes[idx], ativo: checked };
            return {
                ...prev,
                sessoes
            };
        });
    };

    const handleAddSessao = () => {
        setPromptConfig((prev) => {
            const sessoes = [...(prev?.sessoes || [])];
            sessoes.push({
                default: false,
                titulo: "Nova sessão",
                palavrasChave: [],
                agenteResponsavel: { nome: "", idVoz: "" },
                instrunction: "",
                ativo: true
            });
            return {
                ...prev,
                sessoes
            };
        });
    };

    const handleRemoveSessao = (index) => {
        setPromptConfig((prev) => {
            const sessoes = [...(prev?.sessoes || [])];
            sessoes.splice(index, 1);
            return {
                ...prev,
                sessoes
            };
        });
    };

    const handleResetDefault = () => {
        if (!assistantAutocomplete || !assistantAutocomplete.label) return;
        setPromptConfig(buildDefaultPromptConfig(assistantAutocomplete.label));
    };

    // SALVAR: envia só assistantId + PromptJson para a API ------------------

    const handleSave = async () => {
        if (!assistantAutocomplete || !assistantAutocomplete.id) {
            Swal.fire({
                title: "Atenção",
                text: "Selecione um agente antes de salvar.",
                icon: "warning"
            });
            return;
        }

        if (!promptConfig) {
            Swal.fire({
                title: "Atenção",
                text: "Não há configurações para salvar.",
                icon: "warning"
            });
            return;
        }

        Swal.fire({
            backdrop: true,
            allowEscapeKey: false,
            title: "Salvar PromptJson?",
            text: "Deseja salvar a configuração dinâmica de prompt para este agente?",
            color: "#0d0e0e",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Salvar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "black",
            cancelButtonColor: "#F1416C",
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    // valida JSON antes

                    const jsonStr = JSON.stringify(promptConfig, null, 2);
                    try {
                        JSON.parse(jsonStr);
                    } catch (e) {
                        Swal.showValidationMessage("PromptJson gerado não é um JSON válido.");
                        return;
                    }

                    const payload = {
                        promptJson: jsonStr
                    };

                    console.log(JSON.stringify(promptConfig, null, 2));

                    // Ajuste o endpoint conforme você implementar no backend:
                    // ex: Assistant/SavePromptJson
                    const response = await POST_DATA(`Assistant/SavePromptJson/${assistantAutocomplete.id}`, payload);

                    if (!response) {
                        Swal.showValidationMessage("Erro ao salvar PromptJson.");
                        return;
                    }

                    return response;
                } catch (err) {
                    SalvarLogErro("ConfigPromptJson - handleSave", err);
                    Swal.showValidationMessage(`Erro ao salvar: ${err.message}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: "Sucesso!",
                    text: "PromptJson salvo com sucesso.",
                    icon: "success"
                });
            }
        });
    };

    // ----------------------------------------------------------------------

    return (
        <Container>
            {loading && <LoadingAll loading={loading} />}

            <ContainerConfig className="bg-white dark:bg-gray-900 shadow-lg rounded-lg">
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    Configuração dinâmica de Prompt (PromptJson)
                </Typography>

                <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
                    Selecione um agente e configure as sessões dinâmicas (Alimentação, Performance, Notificação, etc.)
                    que serão usadas para montar a instrução enviada para o modelo.
                </Typography>

                {/* Seleção do agente + botão reset */}
                <Box sx={{ mb: 3 }}>
                    <Grid2 container spacing={2} alignItems="center">
                        <Grid2 xs={12} md={8} size={{ xs: 12, sm: 3 }}>
                            <Autocomplete
                                fullWidth
                                disableClearable
                                options={assistants}
                                getOptionLabel={(option) => option.label || ""}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={assistantAutocomplete}
                                onChange={handleSelectAssistant}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Selecione um agente"
                                        size="small"
                                    />
                                )}
                            />
                        </Grid2>

                        <Grid2 xs={12} md={4} display="flex" justifyContent="flex-end" gap={1}>
                            <Button
                                variant="outlined"
                                onClick={handleResetDefault}
                                disabled={!assistantAutocomplete || !assistantAutocomplete.id}
                            >
                                Resetar JSON base
                            </Button>
                            <Button
                                variant="contained"
                                sx={{ background: "black", color: "white", "&:hover": { background: "#222" } }}
                                onClick={handleSave}
                                disabled={!assistantAutocomplete || !assistantAutocomplete.id || !promptConfig}
                            >
                                Salvar PromptJson
                            </Button>
                        </Grid2>
                    </Grid2>
                </Box>

                {/* Formulário principal */}
                {promptConfig && (
                    <Box sx={{ mt: 1 }}>
                        {/* Configuração básica */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Configuração geral
                            </Typography>
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Nome do assistente no Prompt"
                                        size="small"
                                        value={promptConfig.nome || ""}
                                        onChange={(e) => updatePromptConfig("nome", e.target.value)}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Modelo"
                                        size="small"
                                        value={promptConfig.modelo || ""}
                                        onChange={(e) => updatePromptConfig("modelo", e.target.value)}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Temperatura"
                                        size="small"
                                        inputProps={{ step: 0.1, min: 0, max: 2 }}
                                        value={promptConfig.temperatura ?? 0.7}
                                        onChange={(e) =>
                                            updatePromptConfig("temperatura", Number(e.target.value))
                                        }
                                    />
                                </Grid2>
                            </Grid2>

                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                                    Instrunction global (regras principais)
                                </Typography>
                                <Editor
                                    height="40vh"
                                    defaultLanguage="markdown"
                                    theme={themeSelected}
                                    value={promptConfig.instrunction || ""}
                                    onChange={(value) =>
                                        updatePromptConfig("instrunction", value ?? "")
                                    }
                                    options={{
                                        minimap: { enabled: false },
                                        wordWrap: "on"
                                    }}
                                />
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                                    NotInstruction (regras que o modelo NÃO deve seguir)
                                </Typography>
                                <Editor
                                    height="20vh"
                                    defaultLanguage="markdown"
                                    theme={themeSelected}
                                    value={promptConfig.notInstruction || ""}
                                    onChange={(value) =>
                                        updatePromptConfig("notInstruction", value ?? "")
                                    }
                                    options={{
                                        minimap: { enabled: false },
                                        wordWrap: "on"
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Integração */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Integração
                            </Typography>
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="URL da integração"
                                        size="small"
                                        value={promptConfig.integracao?.url || ""}
                                        onChange={(e) => updateIntegracao("url", e.target.value)}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        label="Método (GET, POST...)"
                                        size="small"
                                        value={promptConfig.integracao?.metodo || ""}
                                        onChange={(e) => updateIntegracao("metodo", e.target.value)}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        label="Body.tipoRequisicao"
                                        size="small"
                                        value={promptConfig.integracao?.body?.tipoRequisicao || ""}
                                        onChange={(e) =>
                                            updateIntegracaoBody("tipoRequisicao", e.target.value)
                                        }
                                    />
                                </Grid2>
                            </Grid2>
                        </Box>

                        {/* Sessões dinâmicas */}
                        <Box sx={{ mb: 3 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 1
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Sessões dinâmicas
                                </Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleAddSessao}
                                >
                                    Adicionar sessão
                                </Button>
                            </Box>

                            {(!promptConfig.sessoes || promptConfig.sessoes.length === 0) && (
                                <Typography variant="body2" color="text.secondary">
                                    Nenhuma sessão configurada. Clique em "Adicionar sessão" para criar a
                                    primeira.
                                </Typography>
                            )}

                            <Grid2 container spacing={2} sx={{ mt: 1 }}>
                                {promptConfig.sessoes?.map((sessao, idx) => (
                                    <Grid2 xs={12} key={idx}>
                                        <Card className="bg-gray-50 dark:bg-gray-800">
                                            <CardContent>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        mb: 1
                                                    }}
                                                >
                                                    <TextField
                                                        label="Título da sessão"
                                                        size="small"
                                                        value={sessao.titulo || ""}
                                                        onChange={(e) =>
                                                            updateSessao(idx, "titulo", e.target.value)
                                                        }
                                                    />

                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleRemoveSessao(idx)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        gap: 2,
                                                        mb: 1
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={!!sessao.default}
                                                                onChange={(e) =>
                                                                    handleToggleDefaultSessao(
                                                                        idx,
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Sessão default"
                                                    />

                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={sessao.ativo !== false}
                                                                onChange={(e) =>
                                                                    handleToggleAtivoSessao(
                                                                        idx,
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Ativo"
                                                    />
                                                </Box>

                                                <Grid2 container spacing={2} sx={{ mb: 1 }}>
                                                    <Grid2 size={{ xs: 12 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Palavras-chave (separadas por vírgula)"
                                                            value={parsePalavrasChaveToString(
                                                                sessao.palavrasChave
                                                            )}
                                                            onChange={(e) =>
                                                                updateSessao(
                                                                    idx,
                                                                    "palavrasChave",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </Grid2>
                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Agente responsável (nome)"
                                                            value={
                                                                sessao.agenteResponsavel?.nome || ""
                                                            }
                                                            onChange={(e) =>
                                                                updateSessao(
                                                                    idx,
                                                                    "agenteResponsavel.nome",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </Grid2>
                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Agente responsável (idVoz)"
                                                            value={
                                                                sessao.agenteResponsavel?.idVoz || ""
                                                            }
                                                            onChange={(e) =>
                                                                updateSessao(
                                                                    idx,
                                                                    "agenteResponsavel.idVoz",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </Grid2>
                                                </Grid2>

                                                <Typography
                                                    variant="body2"
                                                    sx={{ mt: 1, mb: 1, color: "text.secondary" }}
                                                >
                                                    Instrunction da sessão (regras específicas)
                                                </Typography>

                                                <Editor
                                                    height="35vh"
                                                    defaultLanguage="markdown"
                                                    theme={themeSelected}
                                                    value={sessao.instrunction || ""}
                                                    onChange={(value) =>
                                                        updateSessao(
                                                            idx,
                                                            "instrunction",
                                                            value ?? ""
                                                        )
                                                    }
                                                    options={{
                                                        minimap: { enabled: false },
                                                        wordWrap: "on"
                                                    }}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid2>
                                ))}
                            </Grid2>
                        </Box>

                        {/* Preview JSON */}
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Pré-visualização do JSON (PromptJson)
                            </Typography>
                            <Editor
                                height="45vh"
                                defaultLanguage="json"
                                value={JSON.stringify(promptConfig, null, 2)}
                                theme={themeSelected}
                                onChange={() => { }}
                                options={{
                                    readOnly: true,
                                    minimap: { enabled: false },
                                    wordWrap: "on"
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </ContainerConfig>
        </Container>
    );
};

export default ConfigPromptJson;
